import { Button } from "@/components/ui/button";
import { ArrowRight, Download } from "lucide-react";
import { generateNHProposalPDF } from "@/lib/nhProposalGenerator";
import { nhDeploymentScale } from "@/lib/northHoustonData";

const pillars = [
  {
    title: "Deployment Scale · Sized to the District",
    body: `${nhDeploymentScale.householdsServed.toLocaleString()} households served per year (3 Spokes × ${nhDeploymentScale.perSpokePortfolio} each), proportional to the District's ~${(nhDeploymentScale.population / 1000).toFixed(1)}k-resident population. Year-1 investment $${(nhDeploymentScale.yearOneBudget / 1_000_000).toFixed(2)}M — roughly $${nhDeploymentScale.perHouseholdMonthly}/household/month.`,
  },
  {
    title: "Initial Case Worker Hires",
    body: `CDBG seed funding for service connectors per district Spoke agency, ramping to ~${nhDeploymentScale.connectorsPerSpokeRange} per Spoke at full deployment, each held to a 1:${nhDeploymentScale.caseloadCap} caseload cap (mix of family + senior track based on intake demand).`,
  },
  {
    title: "Coordinated Referral Protocol",
    body: "Formal MOUs with Lone Star North Harris, HCC North, Workforce Solutions Greater Houston, Harris Health, AAA Houston-Harris, Harris County RIDES, and corridor anchor employers. Quarterly ROI reporting back to the District Board, Houston City Council, Harris County Commissioners, and HUD.",
  },
  {
    title: "Two-Year Outcomes Horizon",
    body: "Each cohort tracked from intake to thriving baseline across stability domains — measurable, fundable, replicable. Annual independent evaluation tied to RFP renewal.",
  },
];

export default function MunicipalPartnershipNH() {
  return (
    <section
      id="partnership"
      data-testid="section-partnership-nh"
      className="bg-[#F9F7F1] py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-8 bg-[#8B0000]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000]">
                08 — District Partnership Framework
              </span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              The District + City + County ask.<br />
              <em className="italic">A non-competing multiplier of the corridor's transformation.</em>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:pt-2 text-[#334155] leading-relaxed">
            <p>
              The North Houston District operates under Texas Local Government
              Code Chapter 376 with its own governing board and the convening
              authority to host Bridge Proposal 02. Houston is a HUD CDBG
              entitlement city; Harris County is a CDBG entitlement county. The
              proposal requests a coordinated multi-jurisdictional CDBG
              public-service sub-recipient award — paired with matching
              philanthropic support and federal coordination — to launch as a
              permanent agency embedded in the district service ecosystem.
            </p>
            <p className="mt-4">
              Bridge multiplies the District's existing investments rather than
              competing with them. Every dollar funds a connector who in turn
              unlocks Lone Star certificate enrollment, Section 8 utilization,
              Medicaid sign-ups, LIHEAP draw, and direct-hire pipelines to the
              corridor's anchor employers — dollars already allocated, finally
              activated.
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#CBD5E1] border border-[#CBD5E1] mb-12">
          {pillars.map((p, i) => (
            <div
              key={p.title}
              data-testid={`partnership-nh-pillar-${i}`}
              className="bg-white p-6 lg:p-7"
            >
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000] mb-3">
                Pillar 0{i + 1}
              </div>
              <h3 className="font-display text-xl text-[#0A192F] leading-snug">{p.title}</h3>
              <p className="mt-3 text-sm text-[#475569] leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>

        <div className="bg-[#0A192F] text-[#F9F7F1] p-8 lg:p-12 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-2">
              The Ask
            </div>
            <h3 className="font-display text-2xl lg:text-4xl leading-tight">
              Fund the Bridge. Activate the corridor for the district that lives in it.
            </h3>
            <p className="mt-4 text-[#CBD5E1] leading-relaxed max-w-2xl">
              We are ready to brief the North Houston District Board, the
              Houston Mayor's Office, Houston City Council District B, and
              Harris County Precinct 1 Commissioner. The model is turnkey. The
              timing aligns with the District's 2026 service-plan cycle and the
              City of Houston's CDBG allocation calendar.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <Button
              data-testid="partnership-nh-schedule-cta"
              asChild
              className="rounded-none bg-[#8B0000] hover:bg-[#A41E1E] text-[#F9F7F1] h-12 font-mono text-xs uppercase tracking-widest"
            >
              <a href="#contact">
                Schedule a Briefing <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
            <Button
              data-testid="partnership-nh-download-cta"
              onClick={generateNHProposalPDF}
              variant="outline"
              className="rounded-none border-[#F9F7F1] bg-transparent text-[#F9F7F1] hover:bg-[#F9F7F1] hover:text-[#0A192F] h-12 font-mono text-xs uppercase tracking-widest"
            >
              <Download className="mr-2 h-4 w-4" /> Download Proposal 02 PDF
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
